
import { Material, Matrix, Mesh, Nullable, PBRMaterial, TransformNode, Vector2, Vector3, VertexData } from '@babylonjs/core';
import { BabylonjsModule, DeviceTier } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import {
  clearArchetype,
  createEntityFactory,
  DEFAULT_RADIUS_RATIO,
  RADIUS_MAX,
  RADIUS_MIN,
  toRadian,
  ZEDCamera,
} from '@modules/standard';
import { World } from 'miniplex';

import {
  DanjiHoMeta,
  DongExtendData,
  DongRenderData,
  ItemHoRenderData,
  NormalMeta,
  RoomtypeColors,
} from '../../APIResponse';
import { TourDanjiCameraAPI } from '../../camera/TourDanjiCameraAPI';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { onUpdateRoomTypeFilter } from '../../observable/TourDanjiObservable';
import { TourDanjiFetcher } from '../../TourDanjiFetcher';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiOptions } from '../../TourDanjiModule';
import {
  Base64,  
  checkApproximaterly,
  distance,
  floatArrayToVector3,
  generateRoomTypeColor,
  generateRoomTypeResponse,
  GetCenterPosition,
  MeshFactory,
  sortIndices,
  unique,
  vector3ToFloatArray,
  vector3ToUniqueKey,
} from '../../utiles';
import { DanjiMesh } from '../component/DanjiMesh';
import { MeshType } from '../component/data/DanjiMesh';
import { Entity as BaseEntity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';

export type DongEntity = {
  /**최초 동 초기화 여부, 1 프레임만 존재 */
  DongInit?: true;
  /**
   * API에서 수신된 순수 동 데이터
   */
  DongData?: DongExtendData;

  IsBlocking?: true;
};

type Entity = DongEntity & BaseEntity;

type BuildingMeshData = {
  vertices: number[];
  indices: number[];
  uvs: number[];
  normals: number[];
  roomTypeId: number;
  wallType: WallType;
};

export interface ItemHoInfo {
  meta: DanjiHoMeta;
  mesh: Mesh;
}

enum WallType {
  NoWindow = 0,
  SmallWindow = 1,
  MediumWindow = 2,
  LargeWindow = 3,
  RoofTop = 4,
}

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class DongSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('DongSystem');
  private readonly UVs = [
    new Vector2(0, 1),
    new Vector2(1, 1),
    new Vector2(1, 0),
    new Vector2(1, 0),
    new Vector2(0, 0),
    new Vector2(0, 1),
  ]

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly danjiOptions;
  private readonly world;
  private readonly scene;
  private readonly dongArchetype;
  private readonly withDongInit;
  private readonly raycastSystem;

  private readonly babylonjs;
  private readonly danjiFetcher;
  private readonly materialManager;
  private readonly cameraAPI;

  private readonly createEntity;
  private roomtypeColors: Nullable<RoomtypeColors> = null;
  private dongEffectMaterials: Map<number, Material>;

  private readonly newDongs = new Array<[dong: Omit<Entity, 'entityId'>, itemHo: ItemHoRenderData[]]>();
  private checkTransparency = false;
  private buildingMeshDataCache: Map<number, BuildingMeshData[]> = new Map();
  
  //-----------------------
  constructor({
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,

    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
  }: {
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;

    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
  }) {
    this.danjiOptions = danjiOptions;

    this.world = world;
    this.babylonjs = babylonjs;
    this.scene = babylonjs.scene;

    this.danjiFetcher = danjiFetcher;
    this.materialManager = materialManager;
    this.cameraAPI = cameraAPI;
    this.raycastSystem = raycastSystem;
    this.createEntity = createEntityFactory(this.world);

    this.dongArchetype = world.with('Dong', 'DanjiMeshes');
    this.withDongInit = world.with('DongInit');

    this.dongEffectMaterials = new Map();
    this.dongArchetype.onEntityAdded.subscribe((entity) => {
      entity.DanjiMeshes.danjiMeshes.forEach((dm) => {
        if (dm.type === MeshType.DONG_EFFECT) {
          dm.meshes.forEach((m) => {
            if (m.subMeshes) {
              m.subMeshes.forEach((sm) => {
                const material = sm.getMaterial();
                if (material && material instanceof PBRMaterial) {
                  if (!this.dongEffectMaterials.has(material.uniqueId)) {
                    this.dongEffectMaterials.set(material.uniqueId, material);
                  }
                }
              });
            }
          });
        }
      });
    });
  }

  dispose() {
    this._selectedDong = undefined;
    this.buildingMeshDataCache.clear();
    clearArchetype(this.dongArchetype);
  }

  // --------------------------------
  private highestDongHeight = 0;
  public getMaxRadius(): number {
    return this.highestDongHeight + RADIUS_MIN + RADIUS_MAX;
  }

  public getMinRadius(): number {
    return RADIUS_MIN;
  }

  public getDefaultRadius(): number {
    return this.getMaxRadius() * DEFAULT_RADIUS_RATIO;
  }

  public getZoomPivotHeight() {
    return this.highestDongHeight * 0.6;
  }

  // --------------------------------

  private get gerateable() {
    return !!this.danjiFetcher.dongs && this.danjiFetcher.dongs.length > 0;
  }
  public async generateDongs(
    rootTransform: TransformNode,
    renderingItemDongOnly: boolean = false,
    exceptRoofTop: boolean = false
  ) {
    if (!this.gerateable) return;
    const roomTypes = this.danjiFetcher.danjiData?.roomTypes;
    const targetItem = this.danjiFetcher.renderData?.itemRenderDatas.find(
      (x) => x.id === this.danjiOptions.itemId
    );
    if (roomTypes) {
      this.roomtypeColors = generateRoomTypeColor(this.danjiFetcher.danjiData?.roomTypes);
      
      const roomTypeResponses = generateRoomTypeResponse(this.danjiFetcher.danjiData?.roomTypes);
      const targetItem = this.danjiFetcher.renderData?.itemRenderDatas.find(
        (x) => x.id === this.danjiOptions.itemId
      );
      roomTypeResponses.forEach((response) => {
        if (response.roomTypeId === targetItem?.danjiRoomTypeId) {
          response.active = true;
        }
      });
      onUpdateRoomTypeFilter.notifyObservers(roomTypeResponses);
    }

    let highestDongHeight = Number.MIN_VALUE;

    this.buildingMeshDataCache = this.generateBuildingMeshData();    

    this.danjiFetcher.renderData?.dongRenderDatas.forEach((dong, i) => {
      if (renderingItemDongOnly) return;

      const dongTransform = new TransformNode(dong.dongName);
      dongTransform.setParent(rootTransform);
      dongTransform.position = dong.position;

      const danjiMeshes: DanjiMesh[] = [];
      const dongMesh = this.createFrameMesh(dong, this.danjiFetcher.dongs?.[i]!);
      dongMesh.parent = dongTransform;
      danjiMeshes.push({
        type: MeshType.DONG_FRAME,
        meshes: [dongMesh],
      });

      dong.roomtypes.forEach((roomType) => {
        const roomTypeWall = this.createRoomTypeWall(dong.areaBuildingId, roomType.meta.룸타입id);
        if (!roomTypeWall) return; 
        roomTypeWall.parent = dongTransform;
        roomTypeWall.isVisible = targetItem ? targetItem.danjiRoomTypeId === roomType.meta.룸타입id : false;        
        danjiMeshes.push({
          type: MeshType.DONG_ROOMTYPE_WALL,
          meshes: [roomTypeWall],
          tags: [roomType.meta.룸타입id.toString()],
        });
      });

      const dongProp = this.createPropMesh(dong);
      dongProp.parent = dongTransform;
      danjiMeshes.push({
        type: MeshType.DONG_PROP,
        meshes: [dongProp],
      });
      if (!exceptRoofTop) {
        const dongRoofTop = this.createRoofTopMesh(dong);
        dongRoofTop.parent = dongTransform;
        danjiMeshes.push({
          type: MeshType.DONG_ROOF_TOP,
          meshes: [dongRoofTop],
        });
      }

      const dongItemHos = this.danjiFetcher.renderData?.itemRenderDatas.filter(
        (x) => x.areaBuildingId === dong.areaBuildingId
      );

      if (dongItemHos?.some((v) => v.id === this.danjiOptions.itemId)) {
        const dongEffect = this.createEffectMesh(this.danjiFetcher.dongs?.[i]!);
        dongEffect.parent = dongTransform;
        danjiMeshes.push({
          type: MeshType.DONG_EFFECT,
          meshes: [dongEffect],
        });
      }

      const boundingBox = dongMesh.getBoundingInfo().boundingBox;
      const dongHeight = boundingBox.maximum.y - boundingBox.minimum.y;

      highestDongHeight = Math.max(highestDongHeight, dongHeight);

      const ext = dongTransform.getHierarchyBoundingVectors();
      const center = ext.max.add(ext.min).scale(0.5);
      const guiPosition = new Vector3(center.x, dong.position.y + dongHeight, center.z);

      this.newDongs.push([
        {
          DongInit: true,
          ...(this.danjiFetcher.dongs?.[i] ? { DongData: this.danjiFetcher.dongs[i] } : {}),
          TransformNode: dongTransform,
          Dong: {
            buildingId: dong.areaBuildingId,
            dongName: dong.dongName,
            dongPosition: dong.position,
            dongCenter: center,
            dongFloors: dong.floors,
            dongHeight: dongHeight,
            extends: ext,
            boundingBox: boundingBox,
          },
          GUIDisplayable: {
            position: guiPosition,
            offset: new Vector3(0, 5, 0),
            occludee: true,
            hide: false,
          },
          DanjiMeshes: {
            danjiMeshes: danjiMeshes,
          },
          DanjiOctree: {
            center: dongMesh.getBoundingInfo().boundingBox.centerWorld,
            octree: dongMesh.createOrUpdateSubmeshesOctree(4),
          },
        },
        dongItemHos ?? [],
      ]);      

      this.createDongInit();
    });

    this.highestDongHeight = highestDongHeight;
  }

  public getDongEntity(buildingId: number) {
    return this.dongArchetype.entities.find((e) => e.Dong.buildingId === buildingId);
  }
  public getDongMesh(buildingId: number) {
    const entity = this.getDongEntity(buildingId);
    return entity?.DanjiMeshes.danjiMeshes.find((dm) => dm.type === MeshType.DONG_FRAME)?.meshes.at(0);
  }

  // --------------------------------
  private _selectedDong: (typeof this.dongArchetype.entities)[number] | undefined;
  public selectDong(buildingId: number) {
    const targetDong = this.dongArchetype.entities.find((e) => e.Dong.buildingId === buildingId);
    if (targetDong) {
      this._selectedDong = targetDong;
    } else {
      console.log('해당 동이 존재하지 않습니다.');
    }
    this.cameraAPI.focusOnDong(targetDong?.Dong.buildingId ?? 0);
  }

  public unselectDong() {
    this._selectedDong = undefined;
  }

  public setVisibleRoomTypeWall(roomTypeId: number, isVisible: boolean) {
    this.dongArchetype.entities.forEach((dong) => {
      dong.DanjiMeshes.danjiMeshes.forEach((mesh) => {
        if (mesh.type === MeshType.DONG_ROOMTYPE_WALL && mesh.tags?.some((t) => t === roomTypeId.toString())) {
          mesh.meshes.forEach((m) => {
            m.isVisible = isVisible;
          });
        }
      });
    });
  }

  public setVisibleMeshes(meshType: MeshType, isVisible: boolean) {
    this.dongArchetype.entities.forEach((dong) => {
      dong.DanjiMeshes.danjiMeshes.forEach((mesh) => {
        if (mesh.type === meshType) {
          mesh.meshes.forEach((m) => {
            m.isVisible = isVisible;
          });
        }
      });
    });
  }

  public beforeRenderExecute = () => {
    this.dongEffectMaterials.forEach((mat) => {
      mat.alpha = (Math.cos(Date.now() * 0.005) + 1) * 0.05;
    });
  };

  private transparencyBlockingDong() {
    if (this.danjiOptions.disableTransparent) return;

    if (this._selectedDong) {
      const camera = this.scene.activeCamera;
      if (camera && camera instanceof ZEDCamera) {
        const cameraPosition = camera.globalPosition;
        const selectedDongPosition = this._selectedDong.Dong.dongCenter;
        const targetCenterDistance = Vector3.Distance(selectedDongPosition, cameraPosition);

        const distances: number[] = this._selectedDong.Dong.boundingBox.vectors.map((v) =>
          Vector3.Distance(v, cameraPosition)
        );

        for (const entity of this.dongArchetype.entities) {
          if (entity.Dong.buildingId === this._selectedDong.Dong.buildingId) continue;

          const distance = Vector3.Distance(entity.Dong.dongCenter, cameraPosition);
          const isClose = distance < targetCenterDistance && this.isCloserThanCamera(camera, entity, distances);
          const isBlocking = isClose && this.isBuildingBlockingView(camera, this._selectedDong, entity);
          this.makeTransparentMeshes(entity, isBlocking);
          this.world.update(entity, 'IsBlocking', isBlocking || undefined);
        }
      }
    }
  }

  private isCloserThanCamera(
    camera: ZEDCamera,
    entity: (typeof this.dongArchetype.entities)[number],
    targetDongDistances: Array<number>
  ): boolean {
    let minDistace = Number.MAX_VALUE;
    entity.Dong.boundingBox.vectors.forEach((v1) => {
      const d = Vector3.Distance(camera.globalPosition, v1);
      if (d < minDistace) minDistace = d;
    });

    let isClose = false;
    targetDongDistances.forEach((d1) => {
      if (minDistace < d1) {
        isClose = true;
        return;
      }
    });

    return isClose;
  }

  private isBuildingBlockingView(
    camera: ZEDCamera,
    targetEntity: (typeof this.dongArchetype.entities)[number],
    dongEntity: (typeof this.dongArchetype.entities)[number]
  ): boolean {
    const target = this.getLargestRectangle(camera, targetEntity);
    const thisDong = this.getLargestRectangle(camera, dongEntity);

    if (target && thisDong) {
      const overlapWidth = Math.max(
        0,
        Math.min(target.left + target.width, thisDong.left + thisDong.width) - Math.max(target.left, thisDong.left)
      );
      const overlapHeight = Math.max(
        0,
        Math.min(target.top + target.height, thisDong.top + thisDong.height) - Math.max(target.top, thisDong.top)
      );

      const overlapArea = overlapWidth * overlapHeight;
      const overlapRate = overlapArea / (target.width * target.height);

      if (overlapRate > 0.15) return true;
    }

    return false;
  }

  private getLargestRectangle(camera: ZEDCamera, entity: (typeof this.dongArchetype.entities)[number]) {
    const scene = this.scene;
    const engine = scene.getEngine();
    const canvas = engine.getRenderingCanvas()!;

    const screenCoordinates = entity.Dong.boundingBox.vectorsWorld.map((point) => {
      const screenPoint = Vector3.Project(point, Matrix.Identity(), scene.getTransformMatrix(), camera.viewport);
      const canvasX = (screenPoint.x * canvas.width) / 2 + canvas.width / 2;
      const canvasY = (-screenPoint.y * canvas.height) / 2 + canvas.height / 2;

      return new Vector2(canvasX, canvasY);
    });

    if (screenCoordinates.length === 0) return null;

    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxX = Number.MIN_VALUE;
    let maxY = Number.MIN_VALUE;

    for (const point of screenCoordinates) {
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
    }

    return {
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  private makeTransparentMeshes(entity: (typeof this.dongArchetype.entities)[number], enable: boolean) {
    entity.DanjiMeshes.danjiMeshes.forEach((dm) =>
      dm.meshes.forEach((m) => {
        m.visibility = enable ? 0.2 : 1;
        m.isPickable = enable ? false : true;
      })
    );
    if (entity.GUIDisplayable) {
      entity.GUIDisplayable.hide = enable;
    }
  }

  private createPropMesh(dong: DongRenderData) {
    let adjustedCoords = new Set<Vector3>();
    const adjustCoodinates = new Array<Array<Vector3[]>>();

    for (let i = 0; i < dong.roomtypes.length; i++) {
      const roomType = dong.roomtypes[i]!;
      const roomTypeCoordinates = new Array<Vector3[]>();
      for (let j = 0; j < roomType.coordinatesArray.length; j++) {
        const coords = floatArrayToVector3(roomType.coordinatesArray[j]);
        const isProp = this.isPropCoordinates(coords, dong.height);

        if (isProp) {
          const adjustedPropCoordinates = this.adjustPropCoordinates(coords);
          adjustedCoords = new Set([...adjustedCoords, ...adjustedPropCoordinates.adjustedCoords]);
          roomTypeCoordinates.push(adjustedPropCoordinates.coords);
        } else {
          roomTypeCoordinates.push(coords);
        }
      }
      adjustCoodinates.push(roomTypeCoordinates);
    }

    const propVerteice = adjustCoodinates
      .flatMap((t) => t.flatMap((tt) => tt))
      .map((v) => (adjustedCoords.has(v) ? v : new Vector3(v.x, dong.height, v.z)));

    const vertexData = new VertexData();
    const propCoordinates = vector3ToFloatArray(propVerteice);
    const propTriangles = [...Array<number>(propCoordinates.length / 3).keys()].reverse();
    const propNormals = new Array<number>();
    VertexData.ComputeNormals(propCoordinates, propTriangles, propNormals);
    vertexData.positions = propCoordinates;
    vertexData.indices = propTriangles;
    vertexData.normals = propNormals;

    const mesh = new Mesh(`${dong.dongName}:Prop`);
    vertexData.applyToMesh(mesh);
    mesh.material = this.materialManager.createMaterial(MaterialType.Dong_LandFill);

    return mesh;
  }

  private createEffectMesh(dong: DongExtendData) {
    const factory = new MeshFactory(`${dong.name}:Effect`, {
      receiveShadows: false,
    });

    dong.roomtypes.forEach((roomtypes) => {
      const vertices = roomtypes.coordinatesArray.slice(1).flatMap((coordinates) => coordinates);
      const indices = [...Array(vertices.length / 3).keys()].reverse();
      const normals = Array<number>();
      VertexData.ComputeNormals(vertices, indices, normals);

      factory.addVertices(
        vertices,
        indices,
        normals,
        [],
        this.materialManager.createMaterial(MaterialType.DongFXMaterial)
      );
    });

    factory.optimize();
    const mesh = factory.create();
    return mesh;
  }

  private isPropCoordinates(coordinate: Vector3[], pivot: number) {
    for (let i = 0; i < coordinate.length; i++) {
      const coord = coordinate[i]!;
      if (checkApproximaterly(coord.y, pivot)) {
        return true;
      }
    }
    return false;
  }

  private adjustPropCoordinates(coords: Array<Vector3>): { coords: Array<Vector3>; adjustedCoords: Set<Vector3> } {
    const vectors = coords.map((x) => new Vector3(x.x, x.y, x.z));
    const min = vectors.reduce((previous, current) => {
      return previous > current.y ? current.y : previous;
    }, Number.POSITIVE_INFINITY);

    const adjustCoords = new Array<Vector3>();
    const adjustedCoords = new Set<Vector3>();
    for (let i = 0; i < coords.length; i = i + 3) {
      const first = coords[i]!;
      const second = coords[i + 1]!;
      const third = coords[i + 2]!;

      if (first.y !== min || second.y !== min || third.y !== min) {
        if (first.y === min) {
          const adjustedFirst = new Vector3(first.x, -99, first.z);
          adjustCoords.push(adjustedFirst);
          adjustedCoords.add(adjustedFirst);
        } else {
          adjustCoords.push(new Vector3(first.x, first.y, first.z));
        }

        if (second.y === min) {
          const adjustedSecond = new Vector3(second.x, -99, second.z);
          adjustCoords.push(adjustedSecond);
          adjustedCoords.add(adjustedSecond);
        } else {
          adjustCoords.push(new Vector3(second.x, second.y, second.z));
        }

        if (third.y === min) {
          const adjustedThird = new Vector3(third.x, -99, third.z);
          adjustCoords.push(adjustedThird);
          adjustedCoords.add(adjustedThird);
        } else {
          adjustCoords.push(new Vector3(third.x, third.y, third.z));
        }
      }
    }
    return {
      coords: adjustCoords,
      adjustedCoords: adjustedCoords,
    };
  }

  private createFrameMesh(dong: DongRenderData, ext: DongExtendData): Mesh {
    const factory = new MeshFactory(`${ext.name}:Frame`, {
      receiveShadows: this.babylonjs.options.deviceTier === DeviceTier.Tier1,
    });

    this.createFrameTop(factory, ext);    
    this.createFrameWindow(factory, dong);

    factory.optimize();
    const mesh = factory.create();

    return mesh;
  }

  private createFrameTop(factory: MeshFactory, ext: DongExtendData) {
    const vertices = ext.roomtypes.flatMap((x) => x.coordinatesArray[0]!);
    const indices = [...Array(vertices.length / 3).keys()].reverse();
    const normals = Array<number>();
    
    VertexData.ComputeNormals(vertices, indices, normals);
    const uvs = this.generateUV(floatArrayToVector3(normals));
    const material = this.materialManager.createMaterial(MaterialType.Dong_Frame_Top);
    factory.addVertices(vertices, indices, normals, uvs, material);
  }

  private generateBuildingMeshData() {
    const buildingMeshData = new Map<number, BuildingMeshData[]>();
    this.danjiFetcher.renderData?.dongRenderDatas.forEach((dong, i) => {
      const ext = this.danjiFetcher.dongs?.[i];
      if (!dong || !ext) return;      
      dong.roomtypes.forEach((roomType) => {
        const vertices = roomType.coordinatesArray.slice(1).flatMap((coordinates) => coordinates);
        if (vertices.length === 0) {
          return;
        }
        
        const angles = Array.from(new Set(ext?.building.hos.filter(x => x.meta.룸타입id === roomType.meta.룸타입id).map(x => x.meta.향)));
        const dongDirections = angles.map(x => {
          const rot = Matrix.RotationY(x * toRadian);
          return Vector3.TransformCoordinates(Vector3.LeftHandedBackwardReadOnly, rot);
        });        
        const indices = [...Array(vertices.length / 3).keys()].reverse();
        const normals = Array<number>();
        VertexData.ComputeNormals(vertices, indices, normals);
        const uvs = this.generateUV(floatArrayToVector3(normals));
        const convertedNormals = floatArrayToVector3(normals);

        // 전체 노말에서 옆면 노말만 추려내기
        const uniqueNormals = unique(
          convertedNormals.filter((x) => {
            return !(Math.abs(x.y) > 0);
          }),
          vector3ToUniqueKey
        );
        const verticesGroupByNormal = new Map<string, NormalMeta[]>(
          uniqueNormals.map((x) => [vector3ToUniqueKey(x), Array<NormalMeta>()])
        );

        // 유효한 노말에 존재하는 버텍스만 가져와서 같은 노말끼리 그룹핑
        for (let i = 0; convertedNormals.length > i; i++) {
          const normal = convertedNormals[i]!.clone();        
          const isSideWallTarget = !dongDirections.some(x => Math.abs(x.cosSimilarity(normal)) > 0.9);          
          const key = vector3ToUniqueKey(convertedNormals[i]!);
          const handle = uniqueNormals.findIndex((n) => vector3ToUniqueKey(n) === key);
          if (0 <= handle) {
            const list = verticesGroupByNormal.get(key) || [];
            list.push({
              vertex: [vertices[i * 3]!, vertices[i * 3 + 1]!, vertices[i * 3 + 2]!],
              index: indices[i]!,
              uvs: [uvs[i * 2]!, uvs[i * 2 + 1]!],
              normal: [normals[i * 3]!, normals[i * 3 + 1]!, normals[i * 3 + 2]!],              
              isSideWallTarget,
            });
            verticesGroupByNormal.set(key, list);
          }
        }

        for (const key of verticesGroupByNormal.keys()) {
          const meta = verticesGroupByNormal.get(key);
          if (!meta) continue;
          let wallType = WallType.NoWindow;
          const vertices = meta.flatMap((x) => x.vertex);
          const idx = sortIndices(...meta.flatMap((x) => x.index));
          const norms = meta.flatMap((x) => x.normal);

          let uvs: number[] = [];
          for (let i = 0; i < meta.length; i+=3) {
            const v0 = meta[i];
            if (!v0) continue;
            const v1 = meta[i + 1] || v0;
            const v2 = meta[i + 2] || v0;
            if (!v0.isSideWallTarget) {
              const d = distance(v0.vertex, v1.vertex);
              if (d < 3.0) {
              } else if (d < 4.0) {
                wallType = WallType.SmallWindow;
              } else if (d < 8.0) {
                wallType = WallType.MediumWindow;
              } else {
                wallType = WallType.LargeWindow;
              }  
            }            
            
            const yOffset = v2!.vertex[1] - v1!.vertex[1];
            const yTiles = Math.abs(yOffset / dong.floorHeight);           
            uvs.push(
              v0.uvs[0], v0.uvs[1] * yTiles,
              v1.uvs[0], v1.uvs[1] * yTiles,
              v2.uvs[0], v2.uvs[1] * yTiles
            );
          }

          const meshDataSet = buildingMeshData.get(dong.areaBuildingId) || [];          
          meshDataSet.push({
            vertices,
            indices: idx,
            uvs,
            normals: norms,
            roomTypeId: roomType.meta.룸타입id,
            wallType,
          });          
          buildingMeshData.set(dong.areaBuildingId, meshDataSet);
        }
      });
    });
    return buildingMeshData;
  }

  private createRoomTypeWall(buildingId: number, roomTypeId: number) {
    const buildingMeshData = this.buildingMeshDataCache.get(buildingId)?.filter((x) => x.roomTypeId === roomTypeId);
    if (!buildingMeshData) return undefined;
    const factory = new MeshFactory(`RoomTypeWall:${buildingId}, ${roomTypeId}`, {
      receiveShadows: this.babylonjs.options.deviceTier === DeviceTier.Tier1,
    });
    
    for (const data of buildingMeshData) {
      if (data.vertices.length === 0) {
        continue;
      }
      let materialType: MaterialType = MaterialType.Dong_WallFrame;
      switch (data.wallType) {
        case WallType.SmallWindow:
          materialType = MaterialType.Dong_WallFrame_S;
          break;
        case WallType.MediumWindow:
          materialType = MaterialType.Dong_WallFrame_M;
          break;
        case WallType.LargeWindow:
          materialType = MaterialType.Dong_WallFrame_L;
          break;
      }
      const color = this.roomtypeColors?.get(data.roomTypeId);
      const mat = this.materialManager.createMaterial(materialType, color?.toHexString());
      if (color) {
        mat._albedoColor = color;
        mat._emissiveColor = color;        
      }
      
      factory.addVertices(data.vertices, data.indices, data.normals, data.uvs, mat);
    }

    factory.optimize();

    return factory.create();
  }

  private createFrameWindow(factory: MeshFactory, dong: DongRenderData) {
    dong.roomtypes.forEach((roomtype) => {  
      const buildingMeshData = this.buildingMeshDataCache.get(dong.areaBuildingId)?.filter((x) => x.roomTypeId === roomtype.meta.룸타입id);
      if (!buildingMeshData) return;

      for (const data of buildingMeshData) {
        if (data.vertices.length === 0) {
          continue;
        }
        let materialType: MaterialType = MaterialType.Dong_WindowFrame;
        switch (data.wallType) {
          case WallType.SmallWindow:
            materialType = MaterialType.Dong_WindowFrame_S;
            break;
          case WallType.MediumWindow:
            materialType = MaterialType.Dong_WindowFrame_M;
            break;
          case WallType.LargeWindow:
            materialType = MaterialType.Dong_WindowFrame_L;
            break;
        }
        const mat = this.materialManager.createMaterial(materialType);        
        factory.addVertices(data.vertices, data.indices, data.normals, data.uvs, mat);
      }
    });
  }

  private generateUV(normals: Vector3[]) {
    const uvs = Array<Vector2>(normals.length);
    for (let i = 0; normals.length > i; ) {
      const normal = normals[i]!;

      if (normal == Vector3.Up() || normal == Vector3.Down()) {
        uvs[i] = Vector2.Zero();
        uvs[i + 1] = Vector2.Zero();
        uvs[i + 2] = Vector2.Zero();
        i += 3;
      } else {
        uvs[i] = this.UVs[0]!;
        uvs[i + 1] = this.UVs[1]!;
        uvs[i + 2] = this.UVs[2]!;
        uvs[i + 3] = this.UVs[3]!;
        uvs[i + 4] = this.UVs[4]!;
        uvs[i + 5] = this.UVs[5]!;
        i += 6;
      } 
    }
    return uvs.flatMap((x) => [x.x, x.y]);
  }

  private createRoofTopMesh(dong: DongRenderData): Mesh {
    const positions = dong.roofTopCoordinates;
    const indices = [...Array(positions.length / 3).keys()].reverse();
    const normals = Array<number>();

    VertexData.ComputeNormals(positions, indices, normals);

    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.normals = normals;

    const mesh = new Mesh(`${dong.dongName}:RoofTop`);
    vertexData.applyToMesh(mesh);
    mesh.material = this.materialManager.createMaterial(MaterialType.Dong_RoofTop);

    return mesh;
  }

  private createItemHoMesh(rootTransform: TransformNode, data: ItemHoRenderData): Mesh {
    let coordinates = new Array<Vector3>();
    const stof = Base64.decodeXZY(data!.hoPolygonBase64);
    data!.hoPolygonBase64 = '';
    const ftov = floatArrayToVector3(stof);
    coordinates.push(...ftov);
    coordinates = coordinates.filter((v, i) => coordinates.indexOf(v) === i);
    const center = GetCenterPosition(coordinates);

    for (let i = 0; i < coordinates.length; ++i) {
      let v = coordinates[i]!;
      let centerPos = center;
      centerPos.y = v.y;
      let dir = v.subtract(centerPos).normalize();
      v = v.add(dir.multiply(new Vector3(0.05, 0.05, 0.05)));

      centerPos = v;
      centerPos.y = center.y;
      dir = v.subtract(centerPos).normalize();
      if (dir.y > 0) {
        v = v.subtract(dir.multiply(new Vector3(0.25, 0.25, 0.25)));
      }

      coordinates[i] = v;
    }

    const vertices = vector3ToFloatArray(coordinates);
    const indices = [...Array(vertices.length / 3).keys()].reverse();
    const normals = Array<number>();
    VertexData.ComputeNormals(vertices, indices, normals);

    const vertexData = new VertexData();
    vertexData.positions = vertices;
    vertexData.indices = indices;
    vertexData.normals = normals;

    const mesh = new Mesh(`${data.ho.meta.동}_${data.ho.meta.호}_item`);
    vertexData.applyToMesh(mesh);
    mesh.parent = rootTransform;
    mesh.material = this.materialManager.createMaterial(MaterialType.ItemFXMaterial);

    return mesh;
  }

  private createDongInit = () => {
    this.newDongs.forEach(([dong, itemHos]) => {
      const dongEntity = this.createEntity(dong);
      itemHos.forEach((itemHo) => {
        const mesh = this.createItemHoMesh(dongEntity.TransformNode!, itemHo);
        const ext = mesh.getHierarchyBoundingVectors();
        const center = ext.max.add(ext.min).scale(0.5);
        const guiPosition = new Vector3(center.x, center.y, center.z);

        this.createEntity<Omit<Entity, 'entityId'>>({
          ItemHo: {
            dongBuildingId: itemHo.areaBuildingId,
            hoPosition: guiPosition,
            extends: ext,
            meta: itemHo.ho.meta,
          },
          GUIDisplayable: {
            position: guiPosition,
            offset: new Vector3(0, 0, 0),
            closestXZPointToCamera: {
              meshes: [mesh],
            },
            hide: false,
          },
          DanjiMeshes: {
            danjiMeshes: [
              {
                type: MeshType.DONG_ITEM_HO,
                meshes: [mesh],
              },
            ],
          },
        });
      });
    });
    this.newDongs.length = 0;
  };

  private cleanupDongInit = () => {
    for (const entity of this.withDongInit) {
      this.world.removeComponent(entity, 'DongInit');
    }
  };

  private whenViewMatrixChanged = () => {
    const camera = this.cameraAPI.camera;
    if (camera) {
      const hit = this.raycastSystem.getCameraRaycastHit();
      if (hit) {
        camera.targetHeightPivot = hit.y + 10;
      }
    }


    const checkTransparency = this.cameraAPI.getCurrentCameraViewType() === 'DanjiView';
    if (!checkTransparency && checkTransparency !== this.checkTransparency) {
      for (const entity of this.dongArchetype.entities) {
        this.makeTransparentMeshes(entity, false);
        this.world.update(entity, 'IsBlocking', undefined);
      }
    }

    if (checkTransparency) this.transparencyBlockingDong();

    this.checkTransparency = checkTransparency;
  };

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'beforeRender',
      run: this.beforeRenderExecute,
    },
    {
      group: 'afterRender',
      run: this.cleanupDongInit,
    },
    {
      group: 'viewMatrixChanged',
      run: this.whenViewMatrixChanged,
    },
  ];
}
